import {
  CircularProgress, Table, TableBody, TableCell, TableFooter, TableRow,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import TablePagination from '@mui/material/TablePagination';
import React from 'react';
import { ErrorsStack } from '../../../../components/ErrorsStack';
import STRING_KEYS from '../../../../language/keys';
import { ENGINE_SEARCH_TABLE_HEADER, TABLE } from '../../../../consts';
import { MatchFlowLogsItem } from './MatchFlowLogsItem';
import { MatchFlowLogsTableHeader } from './MatchFlowLogsTableHeader';
import { style } from './style';
import { MatchFlowLogsNoResults } from './MatchFlowLogsNoResults';
import { MATCH_FLOWS_TABLE_HEADER_COLUMNS } from './consts';

type MatchFlowLogsTableProps = {
  data: MatchFlowResponse[],
  totalCount: number,
  currentPage: number,
  limit: number,
  isLoading: boolean,
  isError: boolean,
  errorMessage: string,
  handlePageChange: (pageNumber: number) => void,
}

export const MatchFlowTable = ({
  data,
  totalCount,
  currentPage,
  limit,
  isLoading,
  errorMessage,
  isError,
  handlePageChange,
}: MatchFlowLogsTableProps):JSX.Element => {
  const { t: translate } = useTranslation();

  const headers = [
    {
      key: 'DROPDOWN',
      value: '',
      isSortable: false,
    },
    {
      key: ENGINE_SEARCH_TABLE_HEADER.RUN_DATE,
      value: translate(STRING_KEYS.RUN_DATE),
      isSortable: false,
    },
    {
      key: ENGINE_SEARCH_TABLE_HEADER.STATUS,
      value: translate(STRING_KEYS.STATUS),
      isSortable: false,
    },
    {
      key: ENGINE_SEARCH_TABLE_HEADER.RUN_ID,
      value: translate(STRING_KEYS.RUN_ID_TITLE),
      isSortable: false,
    },
    {
      key: ENGINE_SEARCH_TABLE_HEADER.RUN_TIME,
      value: translate(STRING_KEYS.RUN_TIME),
      isSortable: false,
    },
  ];

  const renderBody = () => {
    if (isLoading) {
      return (
        <TableRow style={ style.TableRow.sx }>
          <TableCell
            sx={ style.TableCell.sx }
            align="center"
            colSpan={ MATCH_FLOWS_TABLE_HEADER_COLUMNS }
          >
            <CircularProgress />
          </TableCell>
        </TableRow>
      );
    }

    if (data.length === 0 && !isError) {
      return (
        <MatchFlowLogsNoResults />
      );
    }

    return data.map((item: MatchFlowResponse) => {
      return (
        <MatchFlowLogsItem
          id={ item._id }
          key={ item._id }
          status={ item.status }
          runId={ item.matchEngine?.runId }
          executionDuration={ item.matchEngine?.elapsedTimeSeconds }
          createdAt={ item.createdAt }
          inputParameters={ item.inputParameters }
          outputParameters={ item.matchEngine }
        />
      );
    });
  };

  const onPageChange = (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
    handlePageChange(page);
  };

  return (
    <Table>
      {!isLoading && data.length !== 0 && !isError && (
        <MatchFlowLogsTableHeader headers={ headers } />
      )}
      <TableBody>
        { isError && (
          <ErrorsStack
            errorMessage={ errorMessage || translate(STRING_KEYS.DEFAULT_ERROR_MESSAGE) }
            errors={ [] }
          />
        )}
        {renderBody()}
      </TableBody>
      <TableFooter>
        <TableRow>
          <TablePagination
            count={ totalCount }
            page={ currentPage - 1 }
            rowsPerPage={ limit || TABLE.ROWS_PER_PAGE }
            colSpan={ headers.length }
            onPageChange={ onPageChange }
            rowsPerPageOptions={ [] }
          />
        </TableRow>
      </TableFooter>
    </Table>
  );
};
